import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { useToast } from '~/components/feedback';
import { Button, Dialog, Select, Textarea } from '~/components/primitives';
import { ProvenanceChip } from '~/components/status/ProvenanceChip';
import {
  fetchCounterparty,
  fetchCounterpartyUsage,
  moveCounterparty,
  softDeleteCounterparty,
} from '~/lib/api/counterparties';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import { formatBoolean, formatDate } from '~/lib/format';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import cpStyles from './_auth.counterparties.module.css';

/**
 * S14-FE.2 — Counterparty detail page.
 */

// EFE-1 Pass 88: rename .tsx → .index.tsx (parent-without-Outlet bug, как FE-294 ESF).
export const Route = createFileRoute('/_auth/counterparties/$id/')({
  component: CounterpartyDetail,
});

function Row({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div className={shared.detailRow}>
      <span className={shared.detailRowLabel}>{label}</span>
      <strong>{value ?? '—'}</strong>
    </div>
  );
}

// C2 — метка типа зависимого документа в move-alert.
function depTypeLabel(type: string): string {
  if (type === 'invoice') return m.cp_dep_invoices();
  if (type === 'payment') return m.cp_dep_payments();
  if (type === 'settlement') return m.cp_dep_settlements();
  return type;
}

function CounterpartyDetail(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['counterparties', id],
    queryFn: () => fetchCounterparty(id),
  });

  const deleteMut = useMutation({
    mutationFn: () => softDeleteCounterparty(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['counterparties'] }),
  });

  // access-control (F10) — контролируемый перенос в другую компанию.
  const toast = useToast();
  // NB: `?? []` снаружи селектора — иначе новый [] каждый рендер → infinite loop (React #185).
  const memberships = useAuthStore((s) => s.user?.memberships) ?? [];
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [moveReason, setMoveReason] = useState('');
  // F10 rework (evidence-first) — pre-fetch зависимостей при открытии диалога.
  const usageQuery = useQuery({
    queryKey: ['counterparties', id, 'usage'],
    queryFn: () => fetchCounterpartyUsage(id),
    enabled: moveOpen,
  });
  const depsTotal = (usageQuery.data?.references ?? []).reduce((s, r) => s + r.count, 0);
  const moveBlocked = depsTotal > 0;
  const moveMut = useMutation({
    mutationFn: () =>
      moveCounterparty(id, { target_entity_id: moveTarget, reason: moveReason.trim() }),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.cp_move_success() });
      setMoveOpen(false);
      setMoveTarget('');
      setMoveReason('');
      void qc.invalidateQueries({ queryKey: ['counterparties'] });
    },
    onError: (err: unknown) =>
      toast.push({
        variant: 'error',
        title: m.cp_move_title(),
        description: renderApiErrorMessage(err),
      }),
  });

  const cp = query.data;
  if (!cp) {
    return (
      <div>
        <PageHeader
          breadcrumbs={
            <Breadcrumbs
              items={[
                { label: m.phase2_counterparties_title(), to: '/counterparties' },
                { label: query.isLoading ? '…' : 'Not found' },
              ]}
            />
          }
          title={m.phase2_counterparties_title()}
        />
        <div>{query.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_counterparties_title(), to: '/counterparties' },
              { label: cp.name_ru },
            ]}
          />
        }
        // FE-79 honest-audit: H1 truncate >60 chars (string-only API).
        title={cp.name_ru.length > 60 ? `${cp.name_ru.slice(0, 60)}…` : cp.name_ru}
        actions={
          <>
            {/* EFE-1 Pass 88: edit CTA → /counterparties/$id/edit. */}
            <Button
              variant="secondary"
              onClick={() => navigate({ to: '/counterparties/$id/edit', params: { id } })}
            >
              {m.common_button_edit()}
            </Button>
            {/* F90 (Ф6): отправка в 1С — заявкой уровня 3 (Бухгалтер + Руководитель проектов). */}
            <Tier3SendAction
              variant="secondary"
              label={m.cp_button_push_1c()}
              subject={{
                taskType: 'counterparty_push_1c',
                subjectKind: 'counterparty',
                payload: { id },
              }}
            />
            <Button variant="ghost" onClick={() => setMoveOpen(true)}>
              {m.cp_move_button()}
            </Button>
            <Button
              variant="ghost"
              onClick={() => deleteMut.mutate()}
              loading={deleteMut.isPending}
            >
              {m.common_button_delete()}
            </Button>
          </>
        }
      />

      {/* access-control (F10) — диалог контролируемого переноса в другую компанию. */}
      <Dialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        title={m.cp_move_title()}
        footer={
          <>
            <Button variant="secondary" onClick={() => setMoveOpen(false)}>
              {m.common_button_cancel()}
            </Button>
            <Button
              variant="primary"
              disabled={!moveTarget || !moveReason.trim() || moveMut.isPending || moveBlocked}
              loading={moveMut.isPending}
              onClick={() => moveMut.mutate()}
            >
              {m.cp_move_submit()}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {/* C2 — evidence-first: при блокировке показываем ТОЛЬКО alert со счётчиками
              зависимостей (поля свёрнуты); иначе — consequence + поля выбора. */}
          {moveBlocked ? (
            <div role="alert" className={shared.moveAlertWarn}>
              <div>{m.cp_move_blocked_inline()}</div>
              <div className={shared.moveDepCounts}>
                {(usageQuery.data?.references ?? []).map((r) => (
                  <span key={r.type} className={shared.moveDepChip}>
                    {depTypeLabel(r.type)}: {r.count}
                  </span>
                ))}
              </div>
              {/* access-control (move-alert) — ссылки на конкретные зависимые документы
                  (BE отдаёт sample id+label), а не только счётчики по категориям. */}
              {(usageQuery.data?.references ?? []).flatMap((r) =>
                (r.sample ?? []).map((doc) => {
                  const to =
                    r.type === 'invoice'
                      ? '/invoices/$id'
                      : r.type === 'payment'
                        ? '/payments/$id'
                        : null;
                  if (!to) return null;
                  return (
                    <Link
                      key={`${r.type}:${doc.id}`}
                      to={to}
                      params={{ id: doc.id }}
                      className={shared.moveDepLink}
                    >
                      {depTypeLabel(r.type)} · {doc.label}
                    </Link>
                  );
                }),
              )}
            </div>
          ) : (
            <>
              <div className={shared.moveNote}>{m.cp_move_consequence()}</div>
              <Select
                label={m.cp_move_target()}
                required
                value={moveTarget}
                onChange={(e) => setMoveTarget(e.target.value)}
                options={memberships
                  .filter((mem) => mem.entity_id !== cp.entity_id)
                  .filter(
                    (mem, i, arr) => arr.findIndex((x) => x.entity_id === mem.entity_id) === i,
                  )
                  .map((mem) => ({ value: mem.entity_id, label: mem.entity_name }))}
              />
              <Textarea
                label={m.cp_move_reason()}
                required
                rows={2}
                value={moveReason}
                onChange={(e) => setMoveReason(e.target.value)}
                hint={m.cp_move_disabled_hint()}
              />
            </>
          )}
        </div>
      </Dialog>

      <div className={shared.detailCard}>
        {/* integrations F7 — scope: holding CP «Общий (холдинг)» vs компания. */}
        <Row
          label={m.cp_field_company()}
          value={cp.holding_id ? m.cp_scope_holding() : (cp.entity_name ?? null)}
        />
        {/* integrations F7 — provenance источника. */}
        <div className={cpStyles.refRow}>
          <span>{m.cp_column_source()}</span>
          <ProvenanceChip source={cp.source_system} />
          {cp.needs_review ? (
            <span className={cpStyles.reviewBadge} title={m.cp_review_hint()}>
              <AlertTriangle size={11} aria-hidden="true" />
              {m.cp_badge_needs_review()}
            </span>
          ) : null}
        </div>
        {/* integrations B8/F7 — external_refs (skip-null). */}
        {cp.external_refs && cp.external_refs.length > 0 ? (
          <div className={cpStyles.refRow}>
            <span>{m.cp_detail_external_refs()}</span>
            {cp.external_refs.map((ref) => (
              <span key={`${ref.provider_key}:${ref.external_id}`} className={cpStyles.refMono}>
                {ref.provider_key}:{ref.external_id}
              </span>
            ))}
          </div>
        ) : null}
        <Row label={m.cp_column_kind()} value={m.cp_kind_label({ kind: cp.kind })} />
        <Row label={m.cp_column_bin_iin()} value={cp.bin_iin} />
        <Row
          label={m.cp_column_tax_status()}
          value={m.cp_tax_status_label({ tax_status: cp.tax_status })}
        />
        {/* FE-94 sweep: standalone currency display через glyph mapping. */}
        <Row
          label={m.cp_column_currency()}
          value={
            { KZT: '₸ KZT', USD: '$ USD', EUR: '€ EUR', RUB: '₽ RUB', CNY: '¥ CNY' }[
              cp.default_currency
            ] ?? cp.default_currency
          }
        />
        <Row label={m.cp_field_payment_terms()} value={String(cp.payment_terms_days)} />
        {/* FE-81: skip empty optional fields to avoid '—' cascade. */}
        {cp.contract_number ? (
          <Row label={m.cp_field_contract_number()} value={cp.contract_number} />
        ) : null}
        {cp.contract_date ? (
          <Row label={m.cp_field_contract_date()} value={formatDate(cp.contract_date)} />
        ) : null}
        {cp.onec_ref ? <Row label={m.cp_field_onec_ref()} value={cp.onec_ref} /> : null}
        {/* FE-76 follow-up: shared formatBoolean helper. */}
        <Row label={m.cp_column_active()} value={formatBoolean(cp.is_active)} />
      </div>
    </div>
  );
}
